import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

interface Props {
  className?: string;
  tabs: { id: string; content: ReactNode; title: ReactNode }[];
  onChange?: (tab: string) => void;
  defaultActiveTab?: string;
}

const DEFAULT_TABS_ON_CHANGE: NonNullable<Props['onChange']> = () => {};

const Tabs = ({
  className = '',
  tabs,
  onChange = DEFAULT_TABS_ON_CHANGE,
  defaultActiveTab = '',
}: Props) => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab]);

  return (
    <div className={className}>
      <div className="tabs-header">
        <h1>Visualization Builder</h1>
        <div className="tabs-header__buttons">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => {
                onChange?.(tab.id);
                setActiveTab(tab.id);
              }}
              className={activeTab === tab.id ? 'tab-button tab-button--active' : 'tab-button'}
            >
              {tab.title}
            </button>
          ))}
        </div>
      </div>

      <hr />

      <div className="tabs-content">{tabs.find((tab) => tab.id === activeTab)?.content}</div>
    </div>
  );
};

const StyledTabs = styled(Tabs)`
  .tabs-header {
    display: flex;
    justify-content: space-between;
    align-items: self-end;
    flex-wrap: wrap;
    margin-top: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      justify-content: space-between;
      align-items: self-end;
      flex-wrap: nowrap;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints['2xl']}) {
      margin-top: 40px;
    }

    .tabs-header__buttons {
      display: inline-flex;
      overflow-x: auto;
      white-space: nowrap;
      padding: 6px;
      background-color: ${({ theme }) => theme.neutralBackground};
      border-radius: 6px;
      margin-right: 0;
      margin-top: 30px;
      margin-bottom: 42px;

      // Hide scrollbar for Chrome, Safari and Opera
      &::-webkit-scrollbar {
        display: none;
      }

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        margin-right: 48px;
        margin-top: 0;
        margin-bottom: 0;
      }

      > .tab-button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        white-space: nowrap;
        background-color: transparent;
        color: ${({ theme }) => theme.neutralTextWeak};
        padding: 4px 8px;
        border: 0;
        font-weight: 500;
        font-size: ${({ theme }) => theme.fontSizes.sm};
        line-height: ${({ theme }) => theme.lineHeights.sm};
        border-radius: 4px;

        &--active {
          background-color: ${({ theme }) => theme.neutralBackgroundBase};
          color: ${({ theme }) => theme.neutralText};
          box-shadow: 0px 2px 4px 1px #141c2c0a; /* tokens-allow: pending design decision */
        }
      }
    }
    > h1 {
      background: linear-gradient(
        to left,
        /* tokens-allow: pending design decision */ #155676,
        ${({ theme }) => theme.primaryBackgroundStrong}
      );
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.xl};
      line-height: ${({ theme }) => theme.lineHeights.xl};
      color: transparent;
      background-clip: text;
    }
  }

  > hr {
    display: none;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      display: block;
      border-top: 1px solid ${({ theme }) => theme.neutralBorder};
      margin-top: 16px;
      margin-bottom: 16px;
    }
  }

  > .tabs-content {
    height: 100%;
    flex: 1;
    @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
      min-height: 382px;
    }
  }
`;

export default StyledTabs;
